// Site-wide lead capture popup. Appears after 60s on site, once per completion.
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, ArrowRight, ArrowLeft, Home, RefreshCcw, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const RED = "#D41F30";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DONE_KEY = "cw_popup_completed";
const DISMISS_KEY = "cw_popup_dismissed";
const SHOW_AFTER_MS = 60000;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine",
  "Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon",
  "Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia",
  "Washington","West Virginia","Wisconsin","Wyoming",
];

const TIMEFRAMES = ["ASAP, I'm ready now", "1-3 months", "3-6 months", "6+ months"];

export default function LeadPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [goal, setGoal] = useState("");
  const [timeline, setTimeline] = useState("");
  const [state, setState] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle");

  const blocked = useMemo(
    () => /^\/(admin|login|start-journey|get-started)/.test(pathname),
    [pathname]
  );

  useEffect(() => {
    if (localStorage.getItem(DONE_KEY) || sessionStorage.getItem(DISMISS_KEY)) return;
    const preview = new URLSearchParams(window.location.search).get("leadpopup") === "now";
    const t = setTimeout(() => setOpen(true), preview ? 800 : SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  if (!open || blocked) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        timeline,
        location: state,
        state,
        leadType: goal === "refinance" ? "Refinance" : "Homebuying",
        consent: true,
      });
      localStorage.setItem(DONE_KEY, "1");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const pick = (setter, value, nextStep) => () => {
    setter(value);
    setStep(nextStep);
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 focus:border-[#D41F30] transition-all";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="lead-popup"
      style={{ animation: "cwp-fade 0.35s ease both" }}
    >
      <style>{`
        @keyframes cwp-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cwp-card { from { opacity: 0; transform: translateY(36px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes cwp-step { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={dismiss} />
      <div
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-8 md:p-10"
        style={{ animation: "cwp-card 0.45s cubic-bezier(0.22, 1, 0.36, 1) both" }}
      >
        <button
          onClick={dismiss}
          data-testid="lead-popup-close"
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {status === "done" ? (
          <div className="text-center py-6" data-testid="lead-popup-success">
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-display text-3xl font-black text-gray-900 mb-3">You're all set!</h3>
            <p className="text-gray-500 mb-6">A CityWorth expert will reach out shortly with your options.</p>
            <Button onClick={() => setOpen(false)} className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-5 font-semibold" data-testid="lead-popup-done-btn">
              Keep exploring
            </Button>
          </div>
        ) : (
          <>
            {/* progress dots */}
            <div className="flex items-center gap-2 mb-7">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-1.5 rounded-full transition-all duration-500"
                  style={{ width: i === step ? 32 : 14, backgroundColor: i <= step ? RED : "#f3f4f6" }}
                />
              ))}
            </div>

            {step === 0 && (
              <div key="s0" style={{ animation: "cwp-step 0.35s ease both" }}>
                <h3 className="font-display text-2xl md:text-3xl font-black text-gray-900 mb-2">
                  Curious what you qualify for?
                </h3>
                <p className="text-gray-500 mb-6">Takes 30 seconds. No credit impact, no obligation.</p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={pick(setGoal, "buy", 1)}
                    data-testid="popup-goal-buy"
                    className="group border-2 border-gray-200 hover:border-[#D41F30] rounded-2xl p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <Home className="w-8 h-8 mb-3 text-gray-400 group-hover:text-[#D41F30] transition-colors" />
                    <p className="font-display text-lg font-black text-gray-900">Buy a home</p>
                    <p className="text-xs text-gray-400 mt-1">New purchase</p>
                  </button>
                  <button
                    onClick={pick(setGoal, "refinance", 1)}
                    data-testid="popup-goal-refinance"
                    className="group border-2 border-gray-200 hover:border-[#D41F30] rounded-2xl p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <RefreshCcw className="w-8 h-8 mb-3 text-gray-400 group-hover:text-[#D41F30] transition-colors" />
                    <p className="font-display text-lg font-black text-gray-900">Refinance</p>
                    <p className="text-xs text-gray-400 mt-1">Lower my payment</p>
                  </button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div key="s1" style={{ animation: "cwp-step 0.35s ease both" }}>
                <h3 className="font-display text-2xl md:text-3xl font-black text-gray-900 mb-6">
                  What's your estimated timeframe for financing?
                </h3>
                <div className="space-y-3">
                  {TIMEFRAMES.map((t) => (
                    <button
                      key={t}
                      onClick={pick(setTimeline, t, 2)}
                      data-testid={`popup-timeline-${t.split(" ")[0].replace(",", "").toLowerCase()}`}
                      className="w-full border-2 border-gray-200 hover:border-[#D41F30] hover:bg-red-50/40 rounded-2xl px-5 py-4 text-left font-bold text-gray-900 transition-all duration-200 flex items-center justify-between group"
                    >
                      {t}
                      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#D41F30] group-hover:translate-x-1 transition-all" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div key="s2" style={{ animation: "cwp-step 0.35s ease both" }}>
                <h3 className="font-display text-2xl md:text-3xl font-black text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-7 h-7" style={{ color: RED }} /> Select your state
                </h3>
                <p className="text-gray-500 mb-6">Where are you {goal === "refinance" ? "refinancing" : "buying"}?</p>
                <select
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    setStep(3);
                  }}
                  data-testid="popup-state-select"
                  className={`${inputCls} ${state ? "" : "text-gray-400"}`}
                >
                  <option value="" disabled>Choose a state...</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {step === 3 && (
              <form key="s3" onSubmit={submit} style={{ animation: "cwp-step 0.35s ease both" }}>
                <h3 className="font-display text-2xl md:text-3xl font-black text-gray-900 mb-2">
                  Where should we send your options?
                </h3>
                <p className="text-gray-500 mb-6">
                  {goal === "refinance" ? "Refinance" : "Homebuying"} · {timeline} · {state}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input required placeholder="First name" value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} className={inputCls} data-testid="popup-firstname" />
                  <input required placeholder="Last name" value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} className={inputCls} data-testid="popup-lastname" />
                </div>
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`${inputCls} mb-3`} data-testid="popup-email" />
                <input required type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={`${inputCls} mb-5`} data-testid="popup-phone" />
                <Button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="popup-submit"
                  className="w-full bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full py-6 text-base font-semibold shadow-lg transition-all duration-300 hover:scale-[1.01]"
                >
                  {status === "sending" ? (
                    <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Sending...</>
                  ) : (
                    <>See my options <ArrowRight className="ml-2 w-5 h-5" /></>
                  )}
                </Button>
                {status === "error" && (
                  <p className="text-sm text-red-600 mt-3 text-center" data-testid="popup-error">Something went wrong. Please try again.</p>
                )}
                <p className="text-[10px] text-gray-400 mt-4 text-center leading-relaxed">
                  By submitting, you agree CityWorth Mortgage may contact you by phone, email, or text. No obligation, no impact to your credit.
                </p>
              </form>
            )}

            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                data-testid="popup-back-btn"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-gray-400 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
